import { useState, useEffect } from 'react';
import { isNativePlatform } from '../lib/runtime';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PWADetectionState {
  isInstalled: boolean;
  platform: 'ios' | 'android' | 'web' | 'unknown';
  canRequestNotifications: boolean;
}

// ─── Platform Detection ───────────────────────────────────────────────────────

function detectPlatform(): PWADetectionState['platform'] {
  const ua = navigator.userAgent;

  if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
  if (/android/i.test(ua)) return 'android';
  // Covers desktop PWAs installed via Chrome/Edge
  if (typeof window !== 'undefined') return 'web';
  return 'unknown';
}

// ─── Core Detection ───────────────────────────────────────────────────────────

export function detectPWAInstallation(): PWADetectionState {
  const platform = detectPlatform();

  if (isNativePlatform()) {
    return { isInstalled: true, platform, canRequestNotifications: true };
  }

  // Primary check: CSS display-mode media query (iOS 15+, Android, Desktop).
  const standaloneQuery = window.matchMedia('(display-mode: standalone)');
  let isInstalled = standaloneQuery.matches;

  // Fallback for iOS <15: navigator.standalone is not in the official TS lib,
  // so we access it via `any` cast.
  if (!isInstalled && platform === 'ios') {
    isInstalled = (navigator as any).standalone === true;
  }

  // Push notifications require both Service Worker and PushManager support.
  const canRequestNotifications =
    'serviceWorker' in navigator && 'PushManager' in window;

  return { isInstalled, platform, canRequestNotifications };
}

// ─── State Listener ───────────────────────────────────────────────────────────

/**
 * Register a callback that fires whenever the installation state changes
 * (e.g. the user adds the app to their home screen while it's open in
 * the browser). Returns a cleanup function that removes the listener.
 *
 * Note: iOS does not fire this event — once the app is opened in standalone
 * mode the state is fixed for that session. The listener is most useful on
 * Android/Desktop where the browser can promote the PWA install mid-session.
 */
export function onInstallationStateChange(
  callback: (state: PWADetectionState) => void
): () => void {
  const query = window.matchMedia('(display-mode: standalone)');

  const handler = () => callback(detectPWAInstallation());

  // Modern browsers expose `addEventListener` on MediaQueryList; older ones
  // only have the deprecated `addListener`.
  if (typeof query.addEventListener === 'function') {
    query.addEventListener('change', handler);
    return () => query.removeEventListener('change', handler);
  }

  // Fallback for Safari <14 and other older engines.
  query.addListener(handler); // eslint-disable-line deprecation/deprecation
  return () => query.removeListener(handler); // eslint-disable-line deprecation/deprecation
}

// ─── React Hook ───────────────────────────────────────────────────────────────

/**
 * React hook that returns the current PWA installation state and re-renders
 * automatically when the installation status changes.
 *
 * @example
 * const { isInstalled, platform, canRequestNotifications } = useIsInstalledPWA();
 */
export function useIsInstalledPWA(): PWADetectionState {
  const [state, setState] = useState<PWADetectionState>(detectPWAInstallation);

  useEffect(() => {
    // Re-check on mount in case state changed between first render and effect.
    setState(detectPWAInstallation());

    const cleanup = onInstallationStateChange(setState);
    return cleanup;
  }, []);

  return state;
}

// ─── Helper Functions ─────────────────────────────────────────────────────────

/**
 * Returns true when the current device is iOS or Android.
 */
export function isMobileDevice(): boolean {
  const platform = detectPlatform();
  return platform === 'ios' || platform === 'android';
}

/**
 * Human-readable platform name for display in UI or logs.
 */
export function getPlatformDisplayName(): string {
  const platform = detectPlatform();
  const names: Record<typeof platform, string> = {
    ios: 'iOS',
    android: 'Android',
    web: 'Desktop',
    unknown: 'Unknown',
  };
  return names[platform];
}

/**
 * Returns a debug string summarising the current PWA installation state.
 * Intended for development logging and diagnostics.
 */
export function getInstallationStatusMessage(): string {
  const { isInstalled, platform, canRequestNotifications } =
    detectPWAInstallation();

  const parts = [
    `Platform: ${getPlatformDisplayName()}`,
    `Standalone: ${isInstalled ? 'yes' : 'no'}`,
    `Push capable: ${canRequestNotifications ? 'yes' : 'no'}`,
  ];

  return `[PWA] ${parts.join(' | ')}`;
}
